class SearchForm extends HTMLElement {
  constructor() {
    super();
    this.cordingsMode = this.hasAttribute('data-cordings');
    this.mobileQuery = window.matchMedia(this.cordingsMode ? '(max-width: 989px)' : '(max-width: 749px)');

    if (this.cordingsMode) return;

    this.input = this.querySelector('input[type="search"]');
    this.resetButton = this.querySelector('button[type="reset"]');
    this.mobileToggle = this.querySelector('[data-search-toggle]');
    this.searchBar = this.querySelector('[data-search-bar]');

    this.bindInput(this.input);
    this.bindStandardControls();
  }

  connectedCallback() {
    if (!this.cordingsMode || this.localName === 'predictive-search') return;
    this.initCordingsMode();
  }

  initCordingsMode() {
    this.header = this.closest('cordings-header');
    if (!this.header) return;

    this.msearch = this.header.querySelector('[data-msearch]');
    this.searchBar = this.msearch;
    this.inputs = Array.from(this.header.querySelectorAll('.cds-search input[type="search"]'));
    this.inputs.forEach((input) => this.bindInput(input));
  }

  bindInput(input) {
    if (!input) return;

    if (this.cordingsMode) {
      input.addEventListener('input', (event) => {
        this.setActiveInput(event.currentTarget);
        window.clearTimeout(this._inputDebounce);
        this._inputDebounce = window.setTimeout(() => this.onChange(), 300);
      });
      input.addEventListener('focus', (event) => {
        this.setActiveInput(event.currentTarget);
      });
    } else {
      input.addEventListener(
        'input',
        debounce(() => {
          this.onChange();
        }, 300).bind(this)
      );
    }

    input.form?.addEventListener('submit', this.onFormSubmit.bind(this));
    input.form?.addEventListener('reset', (event) => {
      event.preventDefault();
    });
  }

  bindStandardControls() {
    if (this.resetButton) {
      this.resetButton.addEventListener('click', this.onResetButtonClick.bind(this));
    }

    if (this.mobileToggle) {
      this.mobileToggle.addEventListener('click', this.onMobileToggle.bind(this));
      this.mobileQuery.addEventListener('change', () => {
        if (!this.isMobile()) this.closeMobileBar(false);
      });
    }
  }

  setActiveInput(input) {
    if (this.input && this.input !== input) {
      this.input.setAttribute('aria-expanded', 'false');
      this.input.removeAttribute('aria-activedescendant');
    }
    this.input = input;
  }

  isMobile() {
    return this.mobileQuery.matches;
  }

  isMsearchOpen() {
    return Boolean(this.cordingsMode && this.msearch?.classList.contains('is-open'));
  }

  onMobileToggle(event) {
    event.preventDefault();

    if (this.hasAttribute('mobile-bar-open')) {
      this.onMobileToggleClose();
      return;
    }

    this.openMobileBar();
  }

  onMobileToggleClose() {
    this.closeMobileBar(true);
  }

  openMobileBar() {
    if (!this.isMobile() || this.cordingsMode) return;

    this.setAttribute('mobile-bar-open', '');
    this.updateMobileToggleState(true);
    this.afterMobileBarChange();
    this.input?.focus();
  }

  closeMobileBar(clearSearchTerm = true) {
    if (this.cordingsMode) {
      if (!this.msearch?.classList.contains('is-open')) return;
      this.msearch.classList.remove('is-open');
      if (clearSearchTerm && this.input) {
        this.input.value = '';
        this.toggleResetButton();
      }
      this.afterMobileBarChange();
      return;
    }

    if (!this.hasAttribute('mobile-bar-open')) return;

    this.removeAttribute('mobile-bar-open');
    this.updateMobileToggleState(false);

    if (clearSearchTerm && this.input) {
      this.input.value = '';
      this.toggleResetButton();
    }

    this.afterMobileBarChange();
  }

  updateMobileToggleState(isOpen) {
    if (!this.mobileToggle || !this.isMobile()) return;

    const searchLabel = this.mobileToggle.dataset.searchLabel;
    const closeLabel = this.mobileToggle.dataset.closeLabel;

    this.mobileToggle.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    this.mobileToggle.setAttribute('aria-label', isOpen ? closeLabel : searchLabel);
  }

  afterMobileBarChange() {
    this.updateMobileBarPosition();
    this.syncBodyClasses?.();
    this.updateResultsPosition?.();
  }

  updateMobileBarPosition() {
    const anchorBottom = this.getHeaderAnchorBottom?.() ?? document.querySelector('.section-header')?.getBoundingClientRect().bottom ?? 0;

    document.documentElement.style.setProperty('--header-height', `${anchorBottom}px`);
    document.documentElement.style.setProperty('--header-search-bar-height', '0px');
  }

  toggleResetButton() {
    if (!this.resetButton || !this.input) return;

    const resetIsHidden = this.resetButton.classList.contains('hidden');
    if (this.input.value.length > 0 && resetIsHidden) {
      this.resetButton.classList.remove('hidden');
    } else if (this.input.value.length === 0 && !resetIsHidden) {
      this.resetButton.classList.add('hidden');
    }
  }

  onChange() {
    this.toggleResetButton();
  }

  shouldResetForm() {
    return !document.querySelector('[aria-selected="true"] a');
  }

  onResetButtonClick(event) {
    event.preventDefault();
    this.onFormReset(event);
  }

  onFormSubmit() {
    /* Native form submit — query is passed as typed. */
  }

  onFormReset(event) {
    event.preventDefault();
    if (!this.shouldResetForm()) return;

    this.input.value = '';
    this.input.focus();
    this.toggleResetButton();
    this.clearSearchQueryFromUrl();
  }

  isOnSearchResultsPage() {
    const searchPath = new URL(window.routes?.search_url || '/search', window.location.origin).pathname;
    return window.location.pathname === searchPath;
  }

  clearSearchQueryFromUrl() {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('q')) return;

    if (this.isOnSearchResultsPage()) return;

    url.searchParams.delete('q');
    url.searchParams.delete('page');
    url.searchParams.delete('sort_by');

    const query = url.searchParams.toString();
    const nextUrl = `${url.pathname}${query ? `?${query}` : ''}${url.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }
}

customElements.define('search-form', SearchForm);
